import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getAvailableExams, canRetakeExam } from '../../services/exam';
import '../../styles/content-mobile.css';

const ExamList = ({ subjectId = null }) => {
  const { getUserLevel, hasActiveSubscription } = useAuth();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retakeStatus, setRetakeStatus] = useState({});

  useEffect(() => {
    const fetchExams = async () => {
      try {
        console.log('🚀 Starting exam fetch process...');
        
        const levelId = getUserLevel();
        console.log('👤 User level:', levelId);
        
        if (!levelId) {
          console.error('❌ No user level found');
          setError('Unable to determine your education level');
          return;
        }

        // For super admins, get all exams across all levels
        const examsData = levelId === 'all' 
          ? await getAvailableExams(null, subjectId) // Pass null to get all levels
          : await getAvailableExams(levelId, subjectId);
        
        console.log('📊 Exams data received:', examsData);
        setExams(examsData);

        // Check retake eligibility for each exam
        console.log('🔄 Checking retake eligibility...');
        const retakeChecks = await Promise.all(
          examsData.map(async (exam) => {
            const canRetake = await canRetakeExam('current_user_uid', exam.id); // Replace with actual user ID
            return { examId: exam.id, canRetake };
          })
        );

        const retakeMap = {};
        retakeChecks.forEach(({ examId, canRetake }) => {
          retakeMap[examId] = canRetake;
        });
        
        console.log('✅ Retake status map:', retakeMap);
        setRetakeStatus(retakeMap);
        
        console.log('🎉 Exam fetch completed successfully!');
      } catch (err) {
        console.error('💥 Exam fetch failed:', err);
        setError('Failed to load exams. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (hasActiveSubscription()) {
      console.log('💳 User has active subscription, fetching exams...');
      fetchExams();
    } else {
      console.log('🚫 User does not have active subscription');
      setLoading(false);
    }
  }, [getUserLevel, subjectId, hasActiveSubscription]);

  const getDifficultyColor = (percentage) => {
    if (percentage >= 80) return '#10b981'; // green - easy
    if (percentage >= 60) return '#f59e0b'; // yellow - medium
    return '#ef4444'; // red - hard
  };

  const getDifficultyLabel = (percentage) => {
    if (percentage >= 80) return 'Easy';
    if (percentage >= 60) return 'Medium';
    return 'Hard';
  };

  if (!hasActiveSubscription()) {
    return (
      <div className="exam-list">
        <div className="subscription-required">
          <h3>Active Subscription Required</h3>
          <p>You need an active subscription to access practice exams.</p>
          <Link to="/payment/instructions" className="btn btn-primary">
            Activate Subscription
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="exam-list">
        <div className="loading-container">
          <div className="loading-spinner">Loading exams...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="exam-list">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (exams.length === 0) {
    return (
      <div className="exam-list">
        <div className="empty-state">
          <h3>No Exams Available</h3>
          <p>Practice exams for this subject will be available soon.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="exam-list">
      <div className="exams-header">
        <h2>Practice Exams</h2>
        <p>Test your knowledge with our practice exams</p>
      </div>

      <div className="exams-grid">
        {exams.map((exam) => (
          <div key={exam.id} className="exam-card">
            <div className="exam-header">
              <h3>{exam.title}</h3>
              <div className="exam-meta">
                <span className="duration">⏱️ {exam.duration} min</span>
                <span className="questions">📝 {exam.questions.length} questions</span>
              </div>
            </div>

            <div className="exam-details">
              <div className="detail-item">
                <label>Total Marks:</label>
                <span>{exam.totalMarks}</span>
              </div>
              <div className="detail-item">
                <label>Passing Marks:</label>
                <span>{exam.passingMarks}</span>
              </div>
              <div className="detail-item">
                <label>Difficulty:</label>
                <span 
                  className="difficulty-badge"
                  style={{ color: getDifficultyColor((exam.passingMarks / exam.totalMarks) * 100) }}
                >
                  {getDifficultyLabel((exam.passingMarks / exam.totalMarks) * 100)}
                </span>
              </div>
            </div>

            <div className="exam-description">
              <p>{exam.description}</p>
            </div>

            <div className="exam-actions">
              <Link 
                to={`/exam/take/${exam.id}`}
                className="btn btn-primary btn-full"
              >
                Start Exam
              </Link>
              
              {!retakeStatus[exam.id] && (
                <div className="retake-notice">
                  <small>Maximum attempts reached</small>
                </div>
              )}
            </div>

            <div className="exam-footer">
              <div className="exam-tips">
                <h4>Exam Tips:</h4>
                <ul>
                  <li>Read each question carefully</li>
                  <li>Manage your time wisely</li>
                  <li>Review your answers if time permits</li>
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="exam-instructions">
        <h3>Exam Instructions</h3>
        <div className="instructions-grid">
          <div className="instruction-item">
            <div className="instruction-icon">⏰</div>
            <h4>Time Management</h4>
            <p>Each exam has a time limit. The exam will auto-submit when time expires.</p>
          </div>
          
          <div className="instruction-item">
            <div className="instruction-icon">🖥️</div>
            <h4>Fullscreen Mode</h4>
            <p>Exams are conducted in fullscreen mode to prevent distractions.</p>
          </div>
          
          <div className="instruction-item">
            <div className="instruction-icon">📵</div>
            <h4>Internet Connection</h4>
            <p>Ensure you have a stable internet connection before starting.</p>
          </div>
          
          <div className="instruction-item">
            <div className="instruction-icon">🔄</div>
            <h4>Retake Policy</h4>
            <p>You can retake exams up to 3 times to improve your score.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamList;
